import { Components, Theme, ThemeOptions } from '@mui/material/styles';

const getSnackbarTheme = (theme: Theme): ThemeOptions => {
  const snackbarTheme: Components<Theme>['MuiSnackbar'] = {
    styleOverrides: {
      root: {
        // Snackbar container styles
      },
    },
  };

  // ─────────────────────────────────────────────────────────────
  // SNACKBAR MESSAGE CONTAINER BASE STYLES
  // ─────────────────────────────────────────────────────────────
  const snackbarMessageContainerBase = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.unit(2), // 8px
    borderRadius: theme.unit(1), // 4px
    paddingLeft: theme.unit(4), // 16px
    paddingRight: theme.unit(2), // 8px
    paddingTop: theme.unit(1), // 4px
    paddingBottom: theme.unit(1), // 4px
    minHeight: 48,
    width: '100%',
    maxWidth: 344,
  };

  // ─────────────────────────────────────────────────────────────
  // SNACKBAR VARIANT STYLES
  // ─────────────────────────────────────────────────────────────
  const snackbarVariantStyles = {
    error: {
      backgroundColor: theme.palette.State['error-background'],
      border: `1px solid ${theme.palette.State['error-pressed']}`,
      '& .snackbar-icon, & .snackbar-text': {
        color: theme.palette.State['error-pressed'],
      },
    },
    warning: {
      backgroundColor: theme.palette.State['warning-background'],
      border: `1px solid ${theme.palette.State['warning-pressed']}`,
      '& .snackbar-icon, & .snackbar-text': {
        color: theme.palette.State['warning-pressed'],
      },
    },
    success: {
      backgroundColor: theme.palette.State['success-background'],
      border: `1px solid ${theme.palette.State['success-pressed']}`,
      '& .snackbar-icon, & .snackbar-text': {
        color: theme.palette.State['success-pressed'],
      },
    },
    info: {
      backgroundColor: theme.palette.State['info-background'],
      border: `1px solid ${theme.palette.State['info-pressed']}`,
      '& .snackbar-icon, & .snackbar-text': {
        color: theme.palette.State['info-pressed'],
      },
    },
  };

  const snackbarContentTheme: Components<Theme>['MuiSnackbarContent'] = {
    styleOverrides: {
      root: {
        padding: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        minWidth: 'auto',
        // Remove default MUI styling that creates the black box
      },
      message: {
        padding: 0,
        // Base styles for snackbar message container
        '& .snackbar-message-container': {
          ...snackbarMessageContainerBase,
        },
        // Variant-specific styles
        '& .snackbar-message-container[data-variant="error"]': {
          ...snackbarVariantStyles.error,
        },
        '& .snackbar-message-container[data-variant="warning"]': {
          ...snackbarVariantStyles.warning,
        },
        '& .snackbar-message-container[data-variant="success"]': {
          ...snackbarVariantStyles.success,
        },
        '& .snackbar-message-container[data-variant="info"]': {
          ...snackbarVariantStyles.info,
        },
        // Icon container styles
        '& .snackbar-icon': {
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        },
        // Text container styles
        '& .snackbar-text': {
          flex: 1,
          ...theme.typography.M3.label.large,
          fontWeight: 700,
        },
        // Close button container styles
        '& .snackbar-close-button': {
          color: theme.palette.M3.sys.light['on-surface-variant'],
          padding: theme.unit(1), // 4px
          flexShrink: 0,
          width: 32,
          height: 32,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  };

  return {
    components: {
      MuiSnackbar: snackbarTheme,
      MuiSnackbarContent: snackbarContentTheme,
    },
  };
};

export default getSnackbarTheme;
